import {
  ButtonGroup,
  Card,
  Checkbox,
  closeCurrentDialog,
  DataStatus,
  Flag,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, HStack, Text, VStack, Show} from 'platform/foundation';
import * as Yup from 'yup';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  ContractInformationResponseBodyV2,
  PersonResponseBodyV2,
  useBatchCreateConsentMutation,
  useGetConsentTypesQuery,
  useGetCustomerV2Query,
} from '@dms/api/customer';
import {ConsentTemplate, ConsentType} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  getContactPersonPrimaryOptionFormatter,
  getContractSubTitle,
  getContractTitle,
  getLocaleLabel,
  getNaturalPersonFullName,
  handleApiError,
  refreshDmsDatagrid,
  useTenant,
} from '@dms/shared';

import {buildArray, EMPTY_PLACEHOLDER, Nullish} from 'shared/web';

import {getConsentContactPersonId} from '../utils/getConsentContactPersonId';
import {getConsentContactPersonRequirement} from '../utils/getConsentContactPersonRequirement';
import {getConsentTemplateMissingPlaceholdersArg} from '../utils/getConsentTemplateMissingPlaceholdersArg';
import {getCustomerContactPersonId} from '../utils/getCustomerContactPersonId';
import {ConsentTemplateMissingDataAlert} from './ConsentTemplateMissingDataAlert';
import {CustomerDetailAlert} from './CustomerDetailAlert';

type AddConsentsFormValues = {
  consents: {
    isChecked: boolean;
    consentTypeId: string;
    templateIndex: number;
    templates: ConsentTemplate[];
  }[];
  customerContractInformationId: string;
  contactPersonId: string | null;
};
interface AddGdprConsentTypeFormProps {
  customerId: string;
  businessCaseId: string | null;
  contactPersonId?: string | Nullish;
}

export function AddConsentForm(props: AddGdprConsentTypeFormProps) {
  const {isMultiLocale} = useTenant();
  const {
    data,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query({
    customerId: props.customerId,
  });

  const {
    data: consentTypes,
    isLoading,
    isError,
  } = useGetConsentTypesQuery(null, {
    selectFromResult: (result) => ({
      ...result,
      data: result.data?.filter((type) => !!type.templates.length),
    }),
  });
  const [create] = useBatchCreateConsentMutation();

  const contacts = data?.contacts ?? [];

  const defaults: AddConsentsFormValues = {
    customerContractInformationId: data?.contractInformation?.[0]?.id ?? '',
    contactPersonId: getCustomerContactPersonId(data, props.contactPersonId),
    consents: (consentTypes ?? []).map((consentType) => ({
      isChecked: false,
      consentTypeId: consentType.id,
      templates: consentType.templates,
      templateIndex: getPreselectedTemplateIndex(consentType, isMultiLocale, data?.preferredLocale),
    })),
  };

  const contractInformation = data?.contractInformation ?? [];

  const contractInformationOptions = contractInformation.map((info) => ({
    label: getContractTitle(info) ?? EMPTY_PLACEHOLDER,
    value: info.id,
  }));

  const contactPersonOptions = contacts.map((contact) => ({
    label: getContactPersonLabel(contact),
    value: contact.id,
  }));

  const onSubmit: FormSubmitHandler<AddConsentsFormValues> = async (values) => {
    const selectedConsents = values.consents.filter((consent) => consent.isChecked);
    const contactPersonId = getConsentContactPersonId(
      findContractInformation(contractInformation, values.customerContractInformationId)?.legalForm,
      values.contactPersonId
    );
    const body = selectedConsents?.map((consent) => ({
      customerContractInformationId: values.customerContractInformationId,
      contactPersonId,
      templateFileId: consent.templates[consent.templateIndex].fileId ?? '',
      templateTitle: consent.templates[consent.templateIndex].title,
      consentTypeId: consent.consentTypeId,
      customerId: props.customerId,
      businessCaseId: props.businessCaseId,
    }));

    await create({body, customerId: props.customerId})
      .unwrap()
      .then(() => refreshDmsDatagrid('customer-gdpr'))
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  return (
    <DataStatus
      spacing={4}
      isError={isError || isCustomerError}
      isLoading={isLoading || isCustomerLoading}
      isEmpty={!consentTypes?.length}
    >
      <Form
        onSubmit={onSubmit}
        daty-cy="add-consent-form"
        defaultValues={defaults}
        schema={getSchema(contractInformation)}
      >
        {(control, api) => {
          const selectedContractInformationId = api.watch('customerContractInformationId');
          const selectedContactPersonId = api.watch('contactPersonId');
          const selectedLegalForm = findContractInformation(
            contractInformation,
            selectedContractInformationId
          )?.legalForm;
          const contactPersonRequirement = getConsentContactPersonRequirement(selectedLegalForm);
          const isContactPersonMissing =
            contactPersonRequirement === 'required' && isNilOrEmpty(contacts);

          return (
            <VStack spacing={4}>
              <Show when={contractInformationOptions.length === 0}>
                <CustomerDetailAlert
                  customerId={props.customerId}
                  title={i18n.t('entity.gdpr.validations.noContractInformation')}
                />
              </Show>
              <HStack spacing={4}>
                <Box flex={1}>
                  <FormField
                    type="choice"
                    control={control}
                    name="customerContractInformationId"
                    data-testid="add-consent-template-contract"
                    label={i18n.t('entity.customer.labels.customer')}
                    options={contractInformationOptions}
                    formatOptionLabel={getFormatContractOptionLabel(contractInformation)}
                    menuInPortal
                  />
                </Box>
                <Box flex={1}>
                  <Show when={contactPersonRequirement !== 'hidden' && !isNilOrEmpty(contacts)}>
                    <FormField
                      type="choice"
                      control={control}
                      name="contactPersonId"
                      data-testid="add-consent-contact-person"
                      label={i18n.t('entity.customer.labels.contactPerson')}
                      options={contactPersonOptions}
                      formatOptionLabel={getContactPersonPrimaryOptionFormatter(
                        new Set(buildArray<string>().whenNotNil(data?.primaryContactId))
                      )}
                      isRequired={contactPersonRequirement === 'required'}
                      isNotClearable={contactPersonRequirement === 'required'}
                      menuInPortal
                    />
                  </Show>
                </Box>
              </HStack>
              <Show when={isContactPersonMissing}>
                <CustomerDetailAlert
                  customerId={props.customerId}
                  title={i18n.t('entity.gdpr.validations.noContactPerson')}
                  data-testid="add-consent-no-contact-person"
                />
              </Show>

              {consentTypes?.map((type, index) => {
                const isChecked = !!api.watch(`consents.${index}.isChecked`);
                const templateIndex = api.watch(`consents.${index}.templateIndex`);

                const onCheckboxChange = (value: boolean) =>
                  api.setValue(`consents.${index}.isChecked`, value);

                return (
                  <Card key={type.id} variant="inlineGrey">
                    <VStack spacing={4}>
                      <Checkbox
                        data-testid="add-consent-checkbox"
                        onChange={onCheckboxChange}
                        value={isChecked}
                        label={type.name}
                      />
                      <Show when={isChecked}>
                        <FormField
                          type="choice"
                          control={control}
                          data-testid="add-consent-template"
                          name={`consents.${index}.templateIndex` as const}
                          label={i18n.t('entity.gdpr.labels.consentTemplate')}
                          options={
                            isMultiLocale
                              ? getGroupedTemplateOptions(type.templates, data?.preferredLocale)
                              : getTemplateOptions(type.templates)
                          }
                          formatOptionLabel={
                            isMultiLocale ? getFormatTemplateOptionLabel(type.templates) : undefined
                          }
                          menuInPortal
                        />
                        <ConsentTemplateMissingDataAlert
                          data-testid="add-consent-missing-template-data"
                          queryArg={getConsentTemplateMissingPlaceholdersArg({
                            consentTypeId: type.id,
                            template: type.templates[templateIndex],
                            customerId: props.customerId,
                            customerContractInformationId: selectedContractInformationId,
                            legalForm: selectedLegalForm,
                            contactPersonId: selectedContactPersonId,
                          })}
                        />
                      </Show>
                    </VStack>
                  </Card>
                );
              })}
              <ButtonGroup align="right">
                <FormButton
                  control={control}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                  data-testid="consent-form-cancel"
                  title={i18n.t('general.actions.cancel')}
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  data-testid="consent-form-submit"
                  title={i18n.t('general.actions.add')}
                  isDisabled={
                    !isAnyConsentTypeSelected(api.getValues().consents) || isContactPersonMissing
                  }
                />
              </ButtonGroup>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}

const isAnyConsentTypeSelected = (consents: AddConsentsFormValues['consents'] | Nullish) =>
  !!consents?.some((consent) => !!consent.isChecked);

function getContactPersonLabel(contact: PersonResponseBodyV2) {
  const fullName = getNaturalPersonFullName(contact);

  return isNilOrEmpty(fullName) ? i18n.t('entity.customer.labels.noContactPersonName') : fullName;
}

const findContractInformation = (
  contractInformation: ContractInformationResponseBodyV2[],
  contractInformationId: number | string | Nullish
) => contractInformation.find((info) => info.id === contractInformationId);

function getFormatContractOptionLabel(contractInformation: ContractInformationResponseBodyV2[]) {
  return (option: {label?: string | null; value?: number | string | null}): ReactNode => {
    const contract = findContractInformation(contractInformation, option.value);
    const legalFormLabel = isNotNil(contract) ? getContractSubTitle(contract) : null;

    return (
      <HStack spacing={2} align="center">
        <Text size="small">{option.label}</Text>
        <Show when={legalFormLabel}>
          {(legalFormLabel) => <Flag colorScheme="neutral" isSubtle label={legalFormLabel} />}
        </Show>
      </HStack>
    );
  };
}

function getPreselectedTemplateIndex(
  consentType: ConsentType,
  isMultiLocale: boolean,
  preferredLocale: string | Nullish
) {
  const preferredLocaleMatchIndex =
    isMultiLocale && isNotNil(preferredLocale)
      ? consentType.templates?.findIndex((template) => template.locale === preferredLocale)
      : undefined;

  if (isNotNil(preferredLocaleMatchIndex) && preferredLocaleMatchIndex >= 0) {
    return preferredLocaleMatchIndex;
  }

  const index = consentType.templates?.findIndex((template) => template.isPrimary);
  return 0 < index ? index : 0;
}

function getTemplateOptions(templates: ConsentTemplate[]) {
  return templates.map((template, index) => ({
    label: getTemplateTitle(template),
    value: index,
  }));
}

function getGroupedTemplateOptions(templates: ConsentTemplate[], customerLocale: string | Nullish) {
  const recommended = templates
    .map((template, index) => ({template, index}))
    .filter(({template}) => isNotNil(customerLocale) && template.locale === customerLocale);

  const others = templates
    .map((template, index) => ({template, index}))
    .filter(({template}) => !(isNotNil(customerLocale) && template.locale === customerLocale));

  const toOption = ({template, index}: {template: ConsentTemplate; index: number}) => ({
    label: getTemplateTitle(template),
    value: index,
  });

  if (recommended.length === 0) {
    return others.map(toOption);
  }

  return [
    {label: i18n.t('general.labels.recommended'), options: recommended.map(toOption)},
    ...(others.length
      ? [{label: i18n.t('general.labels.others'), options: others.map(toOption)}]
      : []),
  ];
}

function getFormatTemplateOptionLabel(templates: ConsentTemplate[]) {
  return (option: {label?: string | null; value?: number | string | null}): ReactNode => {
    const templateIndex = typeof option.value === 'number' ? option.value : null;
    const locale = isNotNil(templateIndex) ? templates[templateIndex]?.locale : null;
    const localeLabel = isNotNil(locale) ? getLocaleLabel(locale) : null;

    return (
      <HStack spacing={2} align="center">
        <Text size="small">{option.label}</Text>
        <Show when={localeLabel}>
          {(localeLabel) => <Flag colorScheme="neutral" isSubtle label={localeLabel} />}
        </Show>
      </HStack>
    );
  };
}

function getTemplateTitle(template: ConsentTemplate | null) {
  return template?.isPrimary
    ? i18n.t('entity.gdpr.labels.template.defaultTemplateTitle', {title: template.title})
    : (template?.title ?? null);
}

const getSchema = (contractInformation: ContractInformationResponseBodyV2[]) =>
  Yup.object().shape({
    customerContractInformationId: Yup.string().required(
      i18n.t('general.validations.fieldIsRequired')
    ),
    contactPersonId: Yup.string()
      .nullable()
      .when('customerContractInformationId', {
        is: (contractInformationId: string | Nullish) =>
          getConsentContactPersonRequirement(
            findContractInformation(contractInformation, contractInformationId)?.legalForm
          ) === 'required',
        then: Yup.string().nullable().required(i18n.t('general.validations.fieldIsRequired')),
      }),
  });
